import React, { FC, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from 'reactstrap';

import PlanDetail from 'src/pages/subscription-new/components/plan-detail';
import SignUpStepper from 'src/components/stepper';
import SubscriptionDurationContainer from '../components/subscription-duration-container/SubscriptionDurationContainer';
import { Layout } from 'src/components/layout-wrapper/Layout';

import { useViewport, isUserFromAustralia } from 'src/lib/utils';
import colors from 'src/styles/equalution/colors';
import { SubscriptionPageProps } from '../SubscriptionPage';
import { useAppSelector } from 'src/store/hooks';
import TimeIcon from 'src/assets/icons/timeIcon.svg';
import ClientTestimonials from 'src/pages/quiz/components/quize-steps/ClientTestimonials';
import AddonUpsell from '../components/addon-upsell/AddonUpsell';
import {
  getNutritionCoachingPricing,
  getSupplementPricing,
} from '../utils/plans.util';
import { useScrollToTop } from 'src/hooks/useScrollToTop';
import { useCampaign } from 'src/hooks/useCampaign';
import MoneybackGuarantee from '../components/MoneybackGuarantee';

const StandardPlanFlowNew: FC<SubscriptionPageProps> = ({
  planType,
  onCheckoutClick,
  showTwoStepProcess,
  isTwelveMonthParam = false,
  showClientTestimonials = false,
  hideOtherDurations = false,
}) => {
  const { selectedPlanId, allPlansSorted } = useAppSelector(
    state => state.signUp
  );
  const { is10BDayCampaign } = useCampaign();
  const { isMobile, isDesktop } = useViewport();
  const [showAddonsStep, setShowAddonsStep] = useState(false);

  useScrollToTop(showAddonsStep);

  const nutritionCoachingPrice = getNutritionCoachingPricing(allPlansSorted);
  const supplementPackPrice = getSupplementPricing(allPlansSorted);

  const handleCheckoutClick = () => {
    // Non-Australian users go directly to checkout
    if (!isUserFromAustralia()) {
      onCheckoutClick();
      return;
    }

    // Australian users: show addons step
    setShowAddonsStep(true);
  };

  if (showAddonsStep) {
    return (
      <Layout module="login" showLighterColors hideSemiCircle>
        <AddonUpsell
          coachingPrice={Number(nutritionCoachingPrice)}
          supplementsPrice={Number(supplementPackPrice)}
          onClose={() => setShowAddonsStep(false)}
          onContinue={() => onCheckoutClick()}
        />
      </Layout>
    );
  }

  return (
    <Layout module="login">
      <Stack
        alignItems={'center'}
        sx={{
          padding: '1.5rem',
          marginX: 'auto',
          ...(isDesktop && { width: '1000px' }),
          ...(isMobile && { width: '320px' }),
        }}>
        <Stack mb={isMobile ? '24px' : '64px'} alignItems={'center'}>
          {isTwelveMonthParam && <LimitedTimeOfferBanner />}
          <Box width={showTwoStepProcess ? 70 : 100}>
            <SignUpStepper
              activeStep={1}
              steps={showTwoStepProcess ? ['1', '2'] : ['1', '2', '3']}
            />
          </Box>
          <Stack alignItems={'center'}>
            <Box
              textAlign={'center'}
              sx={{
                fontSize: isMobile ? '20px' : '29px',
                fontWeight: 800,
                marginTop: '20px',
              }}>
              Your personalised plan
            </Box>
          </Stack>
        </Stack>

        <Stack
          alignItems={'flex-start'}
          justifyContent={'center'}
          direction={'row'}
          flexWrap={'wrap'}
          gap={'40px'}>
          <Stack
            {...(isMobile ? {} : { flex: 1 })}
            maxWidth={
              isMobile ? '320px' : hideOtherDurations ? '900px' : '600px'
            }
            alignItems={'center'}
            gap={'32px'}>
            <PlanDetail />
            {isMobile && !hideOtherDurations && (
              <SubscriptionDurationContainer
                isTwelveMonthParam={isTwelveMonthParam}
                planType={planType}
              />
            )}
          </Stack>

          {!hideOtherDurations && (
            <Stack
              {...(isMobile ? {} : { flex: 1 })}
              alignItems={'flex-start'}
              gap={'32px'}>
              {!isMobile && (
                <SubscriptionDurationContainer
                  isTwelveMonthParam={isTwelveMonthParam}
                  planType={planType}
                />
              )}
            </Stack>
          )}
        </Stack>
        <Button
          style={{
            width: isMobile ? 300 : 400,
            marginTop: isMobile ? '8px' : '32px',
          }}
          size="md"
          disabled={!selectedPlanId}
          onClick={handleCheckoutClick}>
          Continue to Checkout
        </Button>

        {is10BDayCampaign && (
          <MoneybackGuarantee
            color={colors.OFF_BLACK}
            align="center"
            style={{ marginTop: '12px' }}
          />
        )}

        <Box height={'50px'} />

        {showClientTestimonials && (
          <>
            <Typography
              fontSize={'20px'}
              fontWeight={700}
              color={colors.OFF_BLACK}
              align={'center'}>
              We’ve helped thousands of people hit their transformation goals!
            </Typography>
            <ClientTestimonials />
          </>
        )}
        <Box height={'50px'} />
      </Stack>
    </Layout>
  );
};

const LimitedTimeOfferBanner = () => (
  <Box
    sx={{
      width: '90vw',
      backgroundColor: colors.SALAD_HALF,
      borderRadius: '10px',
      padding: '10px 16px',
      marginBottom: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    }}>
    <img src={TimeIcon} alt="Offer Icon" width={24} height={24} />
    <Box
      component="span"
      sx={{
        fontWeight: 600,
        fontSize: '14px',
        color: colors.CUCUMBER,
      }}>
      Your Limited Time Offer!
    </Box>
  </Box>
);

export default StandardPlanFlowNew;
